import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminReturnsApi } from '../api/sales';
import { adminInventoryApi, adminKolayGelsinApi } from '../api/operations';
import type { ProcessReturnDto, ReturnDisposition, ReturnStatus } from '../api/types';
import { returnStatusValues } from '../api/types';
import { Column, DataTable, SelectFilter, Toolbar } from '../components/DataTable';
import { PageHeader } from '../components/Shell';
import { Badge, Field, FormErrors, Modal, Pagination, Spinner } from '../components/ui';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { Clock, CheckCircle2, Truck, PackageCheck, XCircle } from 'lucide-react';
import { formatDate, money, number } from '../lib/format';
import {
  labelOf,
  returnDispositionLabels,
  returnReasonLabels,
  returnStatusLabels,
} from '../lib/labels';
import { P } from '../lib/permissions';

/* ------------------------------------------------------------------ *
 *  Iade talepleri.
 *
 *  Akis: Requested → Approved → Received → Completed. Urun teslim
 *  alinmadan tamamlama sunucu tarafinda reddedilir (409).
 * ------------------------------------------------------------------ */

export function ReturnsPage() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<ReturnStatus | ''>('');
  const [processing, setProcessing] = useState<number | null>(null);

  const returns = useQuery({
    queryKey: ['admin', 'returns', page, status],
    queryFn: () => adminReturnsApi.search({ page, pageSize: 20 }, status || undefined),
    placeholderData: keepPreviousData,
  });

  const columns: Column<NonNullable<typeof returns.data>['items'][number]>[] = [
    {
      key: 'number',
      header: 'İade',
      cell: (row) => (
        <div>
          <p className="font-medium text-ink-900">{row.returnNumber}</p>
          <p className="text-xs text-ink-400">{formatDate(row.requestedAt)}</p>
          {row.returnTrackingNumber ? (
            <span className="mt-1 inline-block rounded bg-brand-50 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-brand-700">
              {row.returnTrackingNumber}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'order',
      header: 'Sipariş',
      cell: (row) => (
        <Link to={`/siparisler/${row.orderId}`} className="link">
          {row.orderNumber}
        </Link>
      ),
    },
    {
      key: 'reason',
      header: 'Neden',
      hideOnMobile: true,
      cell: (row) => (
        <span className="text-ink-600">{returnReasonLabels[row.reason] ?? row.reason}</span>
      ),
    },
    {
      key: 'lines',
      header: 'Kalem',
      align: 'right',
      cell: (row) => number(row.lines.length),
    },
    {
      key: 'amount',
      header: 'Tutar',
      align: 'right',
      cell: (row) => money(row.approvedAmount ?? row.requestedAmount),
    },
    {
      key: 'status',
      header: 'Durum',
      cell: (row) => <Badge label={labelOf(returnStatusLabels, row.status)} />,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (row) => (
        <button type="button" className="btn-secondary btn-sm" onClick={() => setProcessing(row.id)}>
          İşle
        </button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="İadeler" description="Müşteri iade taleplerini yönetin" />

      <Toolbar>
        <SelectFilter
          label="Durum"
          value={status}
          onChange={(value) => {
            setStatus(value as ReturnStatus | '');
            setPage(1);
          }}
          options={returnStatusValues.map((value) => ({
            value,
            label: returnStatusLabels[value].text,
          }))}
        />
      </Toolbar>

      <DataTable
        columns={columns}
        rows={returns.data?.items ?? []}
        rowKey={(row) => row.id}
        isLoading={returns.isLoading}
        error={returns.error}
        onRetry={() => void returns.refetch()}
        emptyMessage="İade talebi yok."
        footer={
          returns.data ? (
            <Pagination
              page={returns.data.page}
              totalPages={returns.data.totalPages}
              totalCount={returns.data.totalCount}
              onChange={setPage}
            />
          ) : null
        }
      />

      <ProcessReturnModal returnId={processing} onClose={() => setProcessing(null)} />
    </div>
  );
}

function ProcessReturnModal({
  returnId,
  onClose,
}: {
  returnId: number | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [status, setStatus] = useState<ReturnStatus>('Approved');
  const [staffNote, setStaffNote] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvedAmount, setApprovedAmount] = useState<number | null>(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrierName, setCarrierName] = useState('Kolay Gelsin');
  const [locationId, setLocationId] = useState<number | null>(null);
  const [notify, setNotify] = useState(true);
  const [inspection, setInspection] = useState<
    Record<number, { received: number; approved: number; disposition: ReturnDisposition }>
  >({});

  const detail = useQuery({
    queryKey: ['admin', 'return', returnId],
    queryFn: () => adminReturnsApi.getById(returnId!),
    enabled: returnId !== null,
  });

  const locations = useQuery({
    queryKey: ['admin', 'locations'],
    queryFn: () => adminInventoryApi.locations(),
    enabled: returnId !== null,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (detail.data) {
      if (detail.data.returnTrackingNumber) setTrackingNumber(detail.data.returnTrackingNumber);
      if (detail.data.returnCarrierName) setCarrierName(detail.data.returnCarrierName);
      if (detail.data.status) setStatus(detail.data.status);
      if (detail.data.approvedAmount !== null && detail.data.approvedAmount !== undefined) {
        setApprovedAmount(detail.data.approvedAmount);
      } else {
        setApprovedAmount(detail.data.requestedAmount);
      }
    }
  }, [detail.data]);

  const generateKolayGelsinCode = useMutation({
    mutationFn: () => adminKolayGelsinApi.generateReturnCode(returnId!),
    onSuccess: (data) => {
      toast.success('Kolay Gelsin iade takip kodu üretildi.');
      setTrackingNumber(data.returnTrackingNumber);
      setCarrierName(data.carrierName);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'returns'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'return', returnId] });
    },
    onError: (err) => toast.fromError(err, 'İade kodu üretilemedi.'),
  });

  const process = useMutation({
    mutationFn: (dto: ProcessReturnDto) => adminReturnsApi.process(returnId!, dto),
    onSuccess: () => {
      toast.success('İade durumu güncellendi.');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'returns'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'return', returnId] });
      onClose();
    },
    onError: (err) => toast.fromError(err, 'İade güncellenirken bir hata oluştu.'),
  });

  const submitWithStatus = async (
    targetStatus: ReturnStatus,
    customTracking?: string,
    customReason?: string
  ) => {
    const activeTracking = customTracking ?? trackingNumber;
    const finalCarrier = activeTracking ? (carrierName || 'Kolay Gelsin') : carrierName;
    const dto: ProcessReturnDto = {
      status: targetStatus,
      staffNote: staffNote || null,
      rejectionReason:
        targetStatus === 'Rejected'
          ? customReason || rejectionReason || 'İade koşullarına uygun bulunmadı.'
          : null,
      approvedAmount: approvedAmount ?? detail.data?.requestedAmount ?? null,
      returnTrackingNumber: activeTracking || null,
      returnCarrierName: finalCarrier || null,
      returnToLocationId: locationId,
      inspectionLines:
        targetStatus === 'Completed'
          ? Object.entries(inspection).map(([lineId, values]) => ({
              returnLineId: Number(lineId),
              quantityReceived: values.received,
              quantityApproved: values.approved,
              disposition: values.disposition,
            }))
          : [],
      notifyCustomer: notify,
    };
    await process.mutateAsync(dto);
  };

  const handleApproveWithKolayGelsin = async () => {
    try {
      let code = trackingNumber;
      if (!code) {
        const kgRes = await adminKolayGelsinApi.generateReturnCode(returnId!);
        code = kgRes.returnTrackingNumber;
        setTrackingNumber(code);
        setCarrierName(kgRes.carrierName);
      }
      await submitWithStatus('Approved', code);
    } catch (err) {
      toast.fromError(err, 'İade onaylanırken hata oluştu.');
    }
  };

  const data = detail.data;
  const currentStatus = data?.status ?? 'Requested';

  const steps: { key: ReturnStatus; label: string; number: number }[] = [
    { key: 'Requested', label: '1. Talep Edildi', number: 1 },
    { key: 'Approved', label: '2. Onaylandı', number: 2 },
    { key: 'InTransit', label: '3. Kargoda / Yolda', number: 3 },
    { key: 'Received', label: '4. Depoda Alındı', number: 4 },
    { key: 'Completed', label: '5. Ücret İade Edildi', number: 5 },
  ];

  const getStepNumber = (s: ReturnStatus) => {
    switch (s) {
      case 'Requested':
        return 1;
      case 'Approved':
        return 2;
      case 'InTransit':
        return 3;
      case 'Received':
      case 'Inspecting':
        return 4;
      case 'Completed':
        return 5;
      default:
        return 0;
    }
  };

  const activeStepNum = getStepNumber(currentStatus);

  return (
    <Modal
      open={returnId !== null}
      title={data ? `İade Talebi ${data.returnNumber}` : 'İade Yönetimi'}
      description={data ? `Sipariş: ${data.orderNumber}` : undefined}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Kapat
          </button>
          {can(P.orders.returns) && currentStatus !== 'Completed' && currentStatus !== 'Cancelled' ? (
            <button
              type="button"
              className="btn-primary"
              onClick={() => void submitWithStatus(status)}
              disabled={process.isPending}
            >
              {process.isPending ? <Spinner className="h-4 w-4 mr-1" /> : null}
              Bilgileri Güncelle
            </button>
          ) : null}
        </>
      }
    >
      {detail.isLoading ? (
        <div className="py-8 text-center text-sm text-ink-500">
          <Spinner className="mx-auto h-6 w-6 mb-2" />
          İade detayları yükleniyor...
        </div>
      ) : data ? (
        <div className="space-y-5">
          <FormErrors error={process.error} />

          {/* Stepper / Aşama Göstergesi */}
          {currentStatus !== 'Rejected' && currentStatus !== 'Cancelled' ? (
            <div className="rounded-xl border border-ink-200 bg-ink-50/60 p-4">
              <div className="flex items-center justify-between">
                {steps.map((st, idx) => {
                  const isDone = activeStepNum > st.number;
                  const isCurrent = activeStepNum === st.number;
                  return (
                    <div key={st.key} className="flex flex-1 items-center">
                      <div className="flex flex-col items-center flex-1">
                        <div
                          className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-all ${
                            isDone
                              ? 'bg-emerald-600 text-white'
                              : isCurrent
                              ? 'bg-brand-600 text-white ring-4 ring-brand-100 shadow'
                              : 'bg-white border border-ink-300 text-ink-400'
                          }`}
                        >
                          {isDone ? '✓' : st.number}
                        </div>
                        <span
                          className={`mt-1.5 text-[11px] text-center font-medium leading-tight ${
                            isCurrent
                              ? 'text-brand-700 font-bold'
                              : isDone
                              ? 'text-emerald-700'
                              : 'text-ink-400'
                          }`}
                        >
                          {st.label}
                        </span>
                      </div>
                      {idx < steps.length - 1 && (
                        <div
                          className={`h-0.5 flex-1 mx-1 mb-4 transition-all ${
                            activeStepNum > st.number ? 'bg-emerald-500' : 'bg-ink-200'
                          }`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-rose-900">
              <div className="flex items-center gap-2 font-semibold text-sm">
                <XCircle className="h-5 w-5 text-rose-600" />
                <span>Bu İade {currentStatus === 'Rejected' ? 'Reddedilmiştir' : 'İptal Edilmiştir'}</span>
              </div>
              {data.rejectionReason && (
                <p className="mt-1 text-xs text-rose-800">
                  Gerekçe: <strong>{data.rejectionReason}</strong>
                </p>
              )}
            </div>
          )}

          {/* AKILLI HIZLI EYLEM ALANI */}
          {currentStatus === 'Requested' && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-4">
              <div className="flex items-center gap-2 mb-1.5 text-amber-950 font-semibold text-sm">
                <Clock className="h-4 w-4 text-amber-600" />
                <span>Müşteri İade Talebi Bekliyor</span>
              </div>
              <p className="text-xs text-amber-800 mb-4">
                İadeyi onayladığınızda otomatik olarak <strong>Kolay Gelsin ücretsiz kargo iade takip kodu</strong> oluşturulur ve müşteriye e-posta ile iletilir.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="btn-primary text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2 px-3 shadow-sm"
                  disabled={process.isPending || generateKolayGelsinCode.isPending}
                  onClick={handleApproveWithKolayGelsin}
                >
                  {process.isPending || generateKolayGelsinCode.isPending ? (
                    <Spinner className="h-3 w-3 mr-1" />
                  ) : null}
                  ✅ İadeyi Onayla & Kolay Gelsin Kodu Üret
                </button>
                <button
                  type="button"
                  className="btn-secondary text-xs text-rose-600 border-rose-200 hover:bg-rose-50 py-2 px-3"
                  onClick={() => setStatus('Rejected')}
                >
                  ❌ Talebi Reddet
                </button>
              </div>
            </div>
          )}

          {currentStatus === 'Approved' && (
            <div className="rounded-xl border border-sky-200 bg-sky-50/80 p-4">
              <div className="flex items-center gap-2 mb-1.5 text-sky-950 font-semibold text-sm">
                <CheckCircle2 className="h-4 w-4 text-sky-600" />
                <span>İade Onaylandı (Kargo Kodu Hazır)</span>
              </div>
              <p className="text-xs text-sky-800 mb-3">
                Kolay Gelsin kargo takip kodu müşteriye verildi. Müşteri ürünü kargoya teslim ettiğinde veya paket yola çıktığında aşağıdaki butonlarla ilerleyin:
              </p>

              <div className="mb-4 flex items-center justify-between rounded-lg border border-sky-300 bg-white p-3 shadow-sm">
                <div>
                  <span className="text-[10px] font-bold text-sky-700 uppercase tracking-wider block">
                    Kolay Gelsin İade Takip Kodu
                  </span>
                  <span className="font-mono text-base font-bold text-ink-900 tracking-wide">
                    {trackingNumber || 'Oluşturuluyor...'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {trackingNumber ? (
                    <button
                      type="button"
                      className="btn-secondary btn-sm text-xs"
                      onClick={() => {
                        void navigator.clipboard.writeText(trackingNumber);
                        toast.success('İade kodu kopyalandı.');
                      }}
                    >
                      📋 Kopyala
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn-primary btn-sm text-xs"
                      disabled={generateKolayGelsinCode.isPending}
                      onClick={() => generateKolayGelsinCode.mutate()}
                    >
                      {generateKolayGelsinCode.isPending ? <Spinner className="h-3 w-3 mr-1" /> : null}
                      Kod Üret
                    </button>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="btn-primary text-xs bg-sky-600 hover:bg-sky-700 text-white font-medium py-2 px-3 shadow-sm"
                  disabled={process.isPending}
                  onClick={() => void submitWithStatus('InTransit')}
                >
                  🚚 Kargoya Verildi / Yolda
                </button>
                <button
                  type="button"
                  className="btn-secondary text-xs bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100 font-medium py-2 px-3"
                  disabled={process.isPending}
                  onClick={() => void submitWithStatus('Received')}
                >
                  📦 Kargo Depoya Ulaştı (Teslim Alındı)
                </button>
              </div>
            </div>
          )}

          {currentStatus === 'InTransit' && (
            <div className="rounded-xl border border-indigo-200 bg-indigo-50/80 p-4">
              <div className="flex items-center gap-2 mb-1.5 text-indigo-950 font-semibold text-sm">
                <Truck className="h-4 w-4 text-indigo-600" />
                <span>Kargo Deponuza Doğru Yolda</span>
              </div>
              <p className="text-xs text-indigo-800 mb-3">
                Müşteri ürünü Kolay Gelsin'e teslim etti ve paket deponuza doğru sevk ediliyor. Kolay Gelsin kuryesi paketi getirdiğinde depoya ulaştı olarak işaretleyiniz:
              </p>
              <div className="flex items-center justify-between rounded-lg border border-indigo-200 bg-white p-3 mb-3">
                <span className="text-xs text-ink-600">
                  Kolay Gelsin Takip:{' '}
                  <strong className="font-mono text-ink-900">{trackingNumber}</strong>
                </span>
              </div>
              <button
                type="button"
                className="btn-primary text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2 px-3 shadow-sm"
                disabled={process.isPending}
                onClick={() => void submitWithStatus('Received')}
              >
                📦 Kargo Depoya Ulaştı (Teslim Alındı)
              </button>
            </div>
          )}

          {(currentStatus === 'Received' || currentStatus === 'Inspecting') && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 p-4">
              <div className="flex items-center gap-2 mb-1.5 text-emerald-950 font-semibold text-sm">
                <PackageCheck className="h-4 w-4 text-emerald-600" />
                <span>Kargo Depoya Ulaştı (İnceleme & Ücret İadesi)</span>
              </div>
              <p className="text-xs text-emerald-800 mb-4">
                Paket depoya ulaştı. Ürünleri kontrol ettikten sonra doğrudan müşteriye{' '}
                <strong>{money(approvedAmount ?? data.requestedAmount)}</strong> tutarındaki para iadesini tamamlamak için butona basınız:
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="btn-primary text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2 px-4 shadow"
                  disabled={process.isPending}
                  onClick={() => void submitWithStatus('Completed')}
                >
                  {process.isPending ? <Spinner className="h-3 w-3 mr-1" /> : null}
                  💰 İadeyi Tamamla & Ücret İadesini Yap ({money(approvedAmount ?? data.requestedAmount)})
                </button>
                <button
                  type="button"
                  className="btn-secondary text-xs text-rose-600 border-rose-200 hover:bg-rose-50 py-2 px-3"
                  onClick={() => setStatus('Rejected')}
                >
                  ❌ Hasarlı / Kabul Edilemez İadeyi Reddet
                </button>
              </div>
            </div>
          )}

          {currentStatus === 'Completed' && (
            <div className="rounded-xl border border-emerald-300 bg-emerald-100/70 p-4">
              <div className="flex items-center gap-2 text-emerald-950 font-semibold text-sm">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                <span>Bu İade Süreci Başarıyla Tamamlandı</span>
              </div>
              <p className="text-xs text-emerald-900 mt-1">
                Ürünler depoya kabul edildi ve <strong>{money(data.approvedAmount ?? data.requestedAmount)}</strong> tutarındaki ücret iadesi müşterinin ödeme kanalına aktarıldı.
              </p>
            </div>
          )}

          {/* İade Edilen Ürün Kalemleri Listesi */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-ink-500">
                İade Edilen Ürünler
              </span>
              <span className="text-xs text-ink-500">
                Talep Nedeni: <strong>{returnReasonLabels[data.reason] ?? data.reason}</strong>
              </span>
            </div>
            {data.customerNote ? (
              <p className="rounded-lg bg-ink-50 px-3 py-2 text-xs text-ink-700 mb-2">
                Müşteri Açıklaması: {data.customerNote}
              </p>
            ) : null}

            <ul className="divide-y divide-ink-100 rounded-lg border border-ink-200 bg-white">
              {data.lines.map((line) => (
                <li key={line.id} className="p-3">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 shrink-0 overflow-hidden rounded bg-ink-50 border border-ink-200">
                      {line.imageUrl ? (
                        <img src={line.imageUrl} alt="" className="h-full w-full object-cover" />
                      ) : null}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-ink-900">{line.productTitle}</p>
                      <p className="truncate text-xs text-ink-500">
                        {line.variantTitle ? `${line.variantTitle} · ` : ''}
                        {line.sku} · İade Talebi: <strong>{line.quantityRequested} adet</strong>
                      </p>
                    </div>
                    <span className="text-sm font-semibold text-ink-800">{money(line.refundAmount)}</span>
                  </div>

                  {currentStatus === 'Received' || currentStatus === 'Inspecting' ? (
                    <div className="mt-2 grid gap-2 sm:grid-cols-3 pt-2 border-t border-ink-100">
                      <div>
                        <label className="label text-[11px]" htmlFor={`rt-recv-${line.id}`}>
                          Teslim Alınan Adet
                        </label>
                        <input
                          id={`rt-recv-${line.id}`}
                          type="number"
                          min={0}
                          max={line.quantityRequested}
                          className="field py-1 text-xs"
                          value={inspection[line.id]?.received ?? line.quantityRequested}
                          onChange={(event) =>
                            setInspection((current) => ({
                              ...current,
                              [line.id]: {
                                received: Number(event.target.value),
                                approved: current[line.id]?.approved ?? line.quantityRequested,
                                disposition: current[line.id]?.disposition ?? 'Restock',
                              },
                            }))
                          }
                        />
                      </div>
                      <div>
                        <label className="label text-[11px]" htmlFor={`rt-appr-${line.id}`}>
                          Kabul Edilen Adet
                        </label>
                        <input
                          id={`rt-appr-${line.id}`}
                          type="number"
                          min={0}
                          max={line.quantityRequested}
                          className="field py-1 text-xs"
                          value={inspection[line.id]?.approved ?? line.quantityRequested}
                          onChange={(event) =>
                            setInspection((current) => ({
                              ...current,
                              [line.id]: {
                                received: current[line.id]?.received ?? line.quantityRequested,
                                approved: Number(event.target.value),
                                disposition: current[line.id]?.disposition ?? 'Restock',
                              },
                            }))
                          }
                        />
                      </div>
                      <div>
                        <label className="label text-[11px]" htmlFor={`rt-disp-${line.id}`}>
                          Stok Durumu
                        </label>
                        <select
                          id={`rt-disp-${line.id}`}
                          className="field py-1 text-xs"
                          value={inspection[line.id]?.disposition ?? 'Restock'}
                          onChange={(event) =>
                            setInspection((current) => ({
                              ...current,
                              [line.id]: {
                                received: current[line.id]?.received ?? line.quantityRequested,
                                approved: current[line.id]?.approved ?? line.quantityRequested,
                                disposition: event.target.value as ReturnDisposition,
                              },
                            }))
                          }
                        >
                          {(['Restock', 'Damaged', 'Discard', 'ReturnToSupplier'] as ReturnDisposition[]).map(
                            (value) => (
                              <option key={value} value={value}>
                                {returnDispositionLabels[value]}
                              </option>
                            )
                          )}
                        </select>
                      </div>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>

          {/* Reddetme Nedeni Kutusu (Eğer Reddet durumu seçilmişse) */}
          {status === 'Rejected' && (
            <div className="rounded-xl border border-rose-300 bg-rose-50 p-4">
              <Field
                label="İadeyi Reddetme Gerekçesi"
                htmlFor="rt-reject"
                required
                hint="Müşteriye e-posta ile iletilir."
              >
                <textarea
                  id="rt-reject"
                  className="field min-h-20 bg-white"
                  placeholder="Örn: Ürün kullanılmış veya ambalajı zarar görmüş olduğundan iade kabul edilememektedir."
                  value={rejectionReason}
                  onChange={(event) => setRejectionReason(event.target.value)}
                />
              </Field>
              <div className="mt-3 flex justify-end gap-2">
                <button
                  type="button"
                  className="btn-secondary text-xs"
                  onClick={() => setStatus(currentStatus)}
                >
                  Vazgeç
                </button>
                <button
                  type="button"
                  className="btn-primary text-xs bg-rose-600 hover:bg-rose-700 text-white font-medium"
                  disabled={process.isPending || !rejectionReason.trim()}
                  onClick={() => void submitWithStatus('Rejected', undefined, rejectionReason)}
                >
                  {process.isPending ? <Spinner className="h-3 w-3 mr-1" /> : null}
                  Reddetmeyi Kesinleştir
                </button>
              </div>
            </div>
          )}

          {/* İade Ayarları & Personel Notları */}
          <div className="rounded-xl border border-ink-200 bg-white p-4 space-y-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-ink-600 block">
              İade Ayarları & Notlar
            </span>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="İade Edilecek Tutar" htmlFor="rt-amount" hint="Müşteriye geri ödenecek net tutar">
                <input
                  id="rt-amount"
                  type="number"
                  step="0.01"
                  min={0}
                  className="field text-sm"
                  value={approvedAmount ?? ''}
                  onChange={(event) =>
                    setApprovedAmount(event.target.value ? Number(event.target.value) : null)
                  }
                />
              </Field>

              <Field label="Depo / Stok Konumu" htmlFor="rt-location">
                <select
                  id="rt-location"
                  className="field text-sm"
                  value={locationId ?? ''}
                  onChange={(event) =>
                    setLocationId(event.target.value ? Number(event.target.value) : null)
                  }
                >
                  <option value="">Varsayılan Depo</option>
                  {(locations.data ?? []).map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="Personel / Depo Notu" htmlFor="rt-note">
              <textarea
                id="rt-note"
                className="field min-h-16 text-sm"
                placeholder="Yalnızca şirket personeli tarafından görülebilir."
                value={staffNote}
                onChange={(event) => setStaffNote(event.target.value)}
              />
            </Field>

            <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-ink-100">
              <label className="flex items-center gap-2 text-xs text-ink-700 cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-ink-300 text-brand-600"
                  checked={notify}
                  onChange={(event) => setNotify(event.target.checked)}
                />
                Müşteriye e-posta ile bildirim gönder
              </label>

              {/* Durumu Manuel Değiştir Seçeneği */}
              <div className="flex items-center gap-2">
                <label className="text-[11px] text-ink-500" htmlFor="rt-manual-status">
                  Durumu Manuel Ayarla:
                </label>
                <select
                  id="rt-manual-status"
                  className="field text-xs py-1 px-2"
                  value={status}
                  onChange={(event) => setStatus(event.target.value as ReturnStatus)}
                >
                  {returnStatusValues.map((value) => (
                    <option key={value} value={value}>
                      {returnStatusLabels[value].text}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
